import { AZURE_IMPORTS_TYPES } from './azure-imports'
import type { CrtCsvUploadType } from './upload-types'

/**
 * Registry of the datasets a MOPE (monthly CRT cycle) expects. Drives the `missing_imports` reminder:
 * period-scoped entries are listed as received or missing (`getMopeImportPresence` in `@repo/db`);
 * the others are continuously maintained and not part of it.
 *
 * `label` is English-only like every email body; the frontend translates off `mopeImports.<key>`.
 * `path` is a frontend route appended to `FUZE_APP_URL`.
 */
export type MopeImportKind = 'azure' | 'manual'

export type MopeImportEntry = {
  key: MopeImportKey
  kind: MopeImportKind
  label: string
  path: `/crt/${string}`
  uploadType?: CrtCsvUploadType
  /** true → checked for data in the period; false → continuously maintained, not in the reminder. */
  isPeriodScoped: boolean
}

export const MOPE_IMPORT_KEYS = [
  'salesDealActuals',
  'rapForecasts',
  'distributionMarginMix',
  'ipAccruals',
  'calculationRunAccruals',
  'agreements',
  'channelMix',
  'productTypeVariants',
  'budgetAllocations',
  'bpcActuals',
  'costOfGoods',
  'ncProvincialContributions',
  'channelContributions',
  'pricingAssumptions',
  'taxAssumptions',
  'manualInvestments',
  'volumeScenarios',
] as const

export type MopeImportKey = (typeof MOPE_IMPORT_KEYS)[number]

export const MOPE_IMPORT_REGISTRY: readonly MopeImportEntry[] = [
  // Automated Azure imports (blob lands around the 5th)
  {
    key: 'salesDealActuals',
    kind: 'azure',
    label: 'Sales deal actuals',
    path: '/crt/customer-investments/data-sources/sales-deal-actuals',
    uploadType: 'import-sales-deal-actuals-details',
    isPeriodScoped: true,
  },
  {
    key: 'rapForecasts',
    kind: 'azure',
    label: 'RAP forecasts',
    path: '/crt/customer-investments/data-sources/rap-forecasts',
    uploadType: 'import-rap-forecasts-details',
    isPeriodScoped: true,
  },
  {
    key: 'distributionMarginMix',
    kind: 'azure',
    label: 'Distribution margin mix',
    path: '/crt/customer-investments/data-sources/distribution-margin-mix',
    uploadType: 'import-distribution-margin-mix-details',
    isPeriodScoped: true,
  },
  {
    key: 'ipAccruals',
    kind: 'azure',
    label: 'IP accruals',
    path: '/crt/customer-investments/data-sources/ip-accruals',
    uploadType: 'import-ip-accruals-details',
    isPeriodScoped: true,
  },
  {
    key: 'calculationRunAccruals',
    kind: 'azure',
    label: 'Calculation run accruals',
    path: '/crt/customer-investments/data-sources/calculation-run-accruals',
    uploadType: 'import-calculation-run-accruals-details',
    isPeriodScoped: true,
  },
  {
    key: 'agreements',
    kind: 'azure',
    label: 'Agreements',
    path: '/crt/customer-investments/data-sources/agreements',
    uploadType: 'import-agreement-details',
    isPeriodScoped: true,
  },
  {
    key: 'channelMix',
    kind: 'azure',
    label: 'Channel mix',
    path: '/crt/pricing-excise/data-sources/channel-mix',
    uploadType: 'import-channel-mix-details',
    isPeriodScoped: true,
  },
  {
    key: 'productTypeVariants',
    kind: 'azure',
    label: 'Product type variants',
    path: '/crt/materials/data-sources/product-type-variants',
    uploadType: 'import-product-type-variant-details',
    isPeriodScoped: true,
  },
  {
    key: 'budgetAllocations',
    kind: 'azure',
    label: 'Budget allocations',
    path: '/crt/customer-investments/data-sources/budget-allocations',
    uploadType: 'import-budget-allocations-details',
    isPeriodScoped: true,
  },
  // Manual imports / assumptions, versioned by period
  {
    key: 'bpcActuals',
    kind: 'manual',
    label: 'BPC actuals',
    path: '/crt/bpc-actuals',
    uploadType: 'import-bpc-actuals-details',
    isPeriodScoped: true,
  },
  {
    key: 'costOfGoods',
    kind: 'manual',
    label: 'Cost of goods',
    path: '/crt/cost-of-goods',
    uploadType: 'import-cost-of-goods-details',
    isPeriodScoped: true,
  },
  {
    key: 'ncProvincialContributions',
    kind: 'manual',
    label: 'Volumes - NC Provincial contributions',
    path: '/crt/volume-forecasts/nc-provincial-contributions',
    uploadType: 'import-nc-provincial-contributions-details',
    isPeriodScoped: true,
  },
  {
    key: 'channelContributions',
    kind: 'manual',
    label: 'Volumes - Channel contributions',
    path: '/crt/volume-forecasts/channel-contributions',
    uploadType: 'import-channel-contributions-details',
    isPeriodScoped: true,
  },
  // Continuously maintained assumptions (effective-date based, no period column)
  {
    key: 'pricingAssumptions',
    kind: 'manual',
    label: 'Pricing assumptions',
    path: '/crt/pricing-excise/pricing-assumptions',
    isPeriodScoped: false,
  },
  {
    key: 'taxAssumptions',
    kind: 'manual',
    label: 'Tax assumptions',
    path: '/crt/pricing-excise/tax-assumptions',
    isPeriodScoped: false,
  },
  {
    key: 'manualInvestments',
    kind: 'manual',
    label: 'Manual investments',
    path: '/crt/customer-investments/data-sources/manual-investments',
    isPeriodScoped: false,
  },
  {
    key: 'volumeScenarios',
    kind: 'manual',
    label: 'Volume scenarios',
    path: '/crt/volume-scenarios',
    isPeriodScoped: false,
  },
]

export const PERIOD_SCOPED_MOPE_IMPORTS = MOPE_IMPORT_REGISTRY.filter((e) => e.isPeriodScoped)

export type PeriodScopedMopeImportKey = Extract<
  MopeImportKey,
  | 'salesDealActuals'
  | 'rapForecasts'
  | 'distributionMarginMix'
  | 'ipAccruals'
  | 'calculationRunAccruals'
  | 'agreements'
  | 'channelMix'
  | 'productTypeVariants'
  | 'budgetAllocations'
  | 'bpcActuals'
  | 'costOfGoods'
  | 'ncProvincialContributions'
  | 'channelContributions'
>

/** Azure entries whose presence is inferred from `uploads_from_azure` (their tables have no period). */
export const isAzureTrackedMopeImport = (entry: MopeImportEntry) =>
  entry.kind === 'azure' &&
  entry.uploadType !== undefined &&
  entry.uploadType in AZURE_IMPORTS_TYPES
